import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Store, Users, DollarSign, Activity, TrendingUp, ArrowUpRight, ArrowDownRight, Package, Clock } from 'lucide-react';
import { apiClient } from '@dukene/shared';
import { formatDistanceToNow } from 'date-fns';

interface RecentActivity {
  type: 'STORE' | 'ORDER' | 'USER';
  title: string;
  description: string;
  date: string;
  id: string;
}

interface DashboardStats {
  totalRevenue: number;
  revenueChange: number;
  activeStores: number;
  storesChange: number;
  totalOrders: number;
  ordersChange: number;
  totalUsers: number;
  usersChange: number;
  recentActivity: RecentActivity[];
  health: {
    server: number;
    api: number;
    database: string;
  };
}

export default function Dashboard() {
  const [timeRange, setTimeRange] = useState<string>('month');

  // Fetch dashboard stats
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats', timeRange],
    queryFn: async () => {
      return apiClient.get<DashboardStats>(`/analytics/admin/stats?range=${timeRange}`);
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const renderTrend = (change: number) => {
    const isPositive = change >= 0;
    const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
    const colorClass = isPositive ? 'text-black' : 'text-gray-400';
    
    return (
      <div className="flex items-center gap-2 mt-3">
        <Icon className={`w-4 h-4 ${colorClass}`} />
        <span className={`text-sm font-medium ${colorClass}`}>
          {Math.abs(change).toFixed(1)}% from last {timeRange === 'today' ? 'day' : timeRange === 'week' ? 'week' : timeRange === 'year' ? 'year' : 'month'}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-black">Platform Overview</h1>
          <p className="text-gray-500 mt-2">Monitor your platform's performance and key metrics</p>
        </div>
        <select 
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="h-12 px-4 rounded-xl border-2 border-gray-200 bg-white focus:border-black focus:outline-none transition-colors font-medium"
        >
          <option value="today">Today</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
          <option value="year">Last Year</option>
        </select>
      </div>

      {/* Stats Grid - Modern Black & White */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Stores */}
        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Store className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Active Stores</h3>
            {isLoading ? (
              <div className="h-12 bg-gray-100 rounded-lg mt-2 animate-pulse"></div>
            ) : (
              <>
                <p className="text-3xl font-black text-black mt-2">{formatNumber(stats?.activeStores || 0)}</p>
                {renderTrend(stats?.storesChange || 0)}
              </>
            )}
          </div>
        </div>

        {/* Active Users */}
        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Users</h3>
            {isLoading ? (
              <div className="h-12 bg-gray-100 rounded-lg mt-2 animate-pulse"></div>
            ) : (
              <>
                <p className="text-3xl font-black text-black mt-2">{formatNumber(stats?.totalUsers || 0)}</p>
                {renderTrend(stats?.usersChange || 0)}
              </>
            )}
          </div>
        </div>

        {/* Total Revenue */}
        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Revenue</h3>
            {isLoading ? (
              <div className="h-12 bg-gray-100 rounded-lg mt-2 animate-pulse"></div>
            ) : (
              <>
                <p className="text-3xl font-black text-black mt-2">{formatCurrency(stats?.totalRevenue || 0)}</p>
                {renderTrend(stats?.revenueChange || 0)}
              </>
            )}
          </div>
        </div>

        {/* Transactions */}
        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Activity className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Orders</h3>
            {isLoading ? (
              <div className="h-12 bg-gray-100 rounded-lg mt-2 animate-pulse"></div>
            ) : (
              <>
                <p className="text-3xl font-black text-black mt-2">{formatNumber(stats?.totalOrders || 0)}</p>
                {renderTrend(stats?.ordersChange || 0)}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity & Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-black text-black">Recent Activity</h2>
            <button className="text-xs font-bold text-gray-400 hover:text-black uppercase tracking-wider">
              View All
            </button>
          </div>
          <div className="space-y-4">
            {isLoading ? (
              [1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 animate-pulse">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))
            ) : stats?.recentActivity && stats.recentActivity.length > 0 ? (
              stats.recentActivity.map((activity) => (
                <div key={`${activity.type}-${activity.id}`} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                    {activity.type === 'STORE' && <Store className="w-5 h-5 text-white" />}
                    {activity.type === 'ORDER' && <Package className="w-5 h-5 text-white" />}
                    {activity.type === 'USER' && <Users className="w-5 h-5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-black truncate">{activity.title}</p>
                    <p className="text-xs text-gray-500 truncate">{activity.description}</p>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-bold text-gray-400 whitespace-nowrap">
                    <Clock className="w-3 h-3" />
                    {formatDistanceToNow(new Date(activity.date), { addSuffix: true })}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50">
                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-black truncate">No recent activity</p>
                  <p className="text-xs text-gray-500">System ready for use</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-black text-white rounded-2xl p-8">
          <div className="mb-6">
            <h2 className="text-xl font-black">Platform Health</h2>
            <p className="text-sm text-gray-400 mt-1">System performance metrics</p>
          </div>
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">Server Status</span>
                <span className="text-sm font-bold text-white">{stats?.health?.server || 0}%</span>
              </div>
              <div className="w-full bg-gray-800 rounded-full h-2">
                <div className="bg-white h-2 rounded-full" style={{ width: `${stats?.health?.server || 0}%` }}></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">API Uptime</span>
                <span className="text-sm font-bold text-white">{stats?.health?.api || 0}%</span>
              </div>
              <div className="w-full bg-gray-800 rounded-full h-2">
                <div className="bg-white h-2 rounded-full" style={{ width: `${stats?.health?.api || 0}%` }}></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">Database</span>
                <span className="text-sm font-bold text-white">{stats?.health?.database || 'Unknown'}</span>
              </div>
              <div className="w-full bg-gray-800 rounded-full h-2">
                <div className="bg-white h-2 rounded-full" style={{ width: '100%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Getting Started Section */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl p-8 text-white">
        <div className="flex items-start justify-between">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-black mb-3">Welcome to Dukene Platform</h2>
            <p className="text-gray-300 mb-6">
              Manage your e-commerce platform with powerful admin tools. Monitor stores, users, and transactions all in one place.
            </p>
            <div className="flex items-center gap-4">
              <button className="px-6 py-3 bg-white text-black rounded-xl font-bold text-sm hover:bg-gray-100 transition-colors">
                Quick Start Guide
              </button>
              <button className="px-6 py-3 bg-transparent border-2 border-white text-white rounded-xl font-bold text-sm hover:bg-white hover:text-black transition-colors">
                Documentation
              </button>
            </div>
          </div>
          <div className="hidden lg:block">
            <TrendingUp className="w-32 h-32 text-gray-700" />
          </div>
        </div>
      </div>
    </div>
  );
}
